import { create } from 'zustand';
import {
  readDashboardLayout,
  type CustomerToCallDto,
  type DashboardSection,
  type DashboardSummaryDto,
  type FollowUpDto,
  type FunnelReportDto,
  type LowStockDto,
  type OrderDto,
  type SalesPointDto,
  type TopProductDto,
} from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';
import { lastDays } from '@/lib/date-range';
import { can } from '@/lib/session';
import { useAuthStore } from './auth.store';
import { usePreferencesStore } from './preferences.store';

/**
 * The overview screen's data.
 *
 * Independent reads that must not take each other down: if low stock fails,
 * the revenue figure still renders. A dashboard that goes blank because one
 * widget's query timed out is worse than one showing three of four panels.
 *
 * One period scopes every figure that has one — the cards, their comparison
 * with the period before, the chart and the best sellers — so the numbers on
 * the screen always agree. The queue and low stock are "now", not a period.
 * A panel this person may not read is not asked for: a 403 is not a figure —
 * nor is a section they hid from their overview (ADR-109).
 */

export type OverviewPeriod = 7 | 30 | 90;

interface OverviewState {
  days: OverviewPeriod;
  summary: DashboardSummaryDto | null;
  trend: SalesPointDto[];
  topSellers: TopProductDto[];
  /** Orders to fulfil, longest waiting first. */
  queue: OrderDto[];
  lowStock: LowStockDto[];
  /** At risk now and nobody on it (ADR-104), the biggest spenders first. */
  toCall: CustomerToCallDto[];
  recentOrders: OrderDto[];
  /** The funnel in brief, over the same period (ADR-108/109). */
  visits: FunnelReportDto | null;
  /** My open follow-ups, the most urgent first (ADR-104/109). */
  myFollowUps: FollowUpDto[];
  /** The first read: panels show their skeletons. */
  loading: boolean;
  /** A new period: panels keep their last picture, faded, until it lands. */
  refreshing: boolean;
  error: string | null;

  load: (days?: OverviewPeriod) => Promise<void>;
}

const QUEUE_ROWS = 6;
const RECENT_ORDERS = 6;
const LOW_STOCK_ROWS = 5;
const TO_CALL_ROWS = 5;
const MY_FOLLOW_UPS = 5;

export const useOverviewStore = create<OverviewState>((set, get) => ({
  days: 30,
  summary: null,
  trend: [],
  topSellers: [],
  queue: [],
  lowStock: [],
  toCall: [],
  recentOrders: [],
  visits: null,
  myFollowUps: [],
  loading: false,
  refreshing: false,
  error: null,

  async load(days) {
    const period = days ?? get().days;
    const first = get().summary === null && get().recentOrders.length === 0;
    set({ days: period, loading: first, refreshing: !first, error: null });

    const user = useAuthStore.getState().user;
    const may = (permission: string) => can(user, permission);
    const unless = <T>(allowed: boolean, call: () => Promise<T>): Promise<T | null> =>
      allowed ? withSession(call) : Promise.resolve(null);
    // Their own overview: a hidden section is not read.
    const layout = readDashboardLayout(usePreferencesStore.getState().preferences?.dashboard);
    const shown = (id: DashboardSection) => !layout.sections.find((section) => section.id === id)?.hidden;

    const [summary, trend, topSellers, queue, lowStock, orders, toCall, visits, myFollowUps] = await Promise.allSettled([
      unless(may('dashboard:read'), () => adminApi.reports.summary({ days: period })),
      unless(may('dashboard:read') && shown('salesTrend'), () => adminApi.reports.salesTrend({ days: period })),
      unless(may('dashboard:read') && shown('topSellers'), () => adminApi.reports.topSellers({ days: period })),
      unless(may('orders:read') && shown('actionQueue'), () =>
        adminApi.orders.list({
          status: ['PAID', 'AWAITING_COLLECTION', 'PROCESSING'],
          sort: 'oldest',
          limit: QUEUE_ROWS,
        }),
      ),
      unless(may('inventory:manage') && shown('lowStock'), () => adminApi.inventory.lowStock({ limit: LOW_STOCK_ROWS })),
      unless(may('orders:read') && shown('recentOrders'), () => adminApi.orders.list({ limit: RECENT_ORDERS })),
      unless(may('customers:read') && shown('customersToCall'), () => adminApi.customers.toCall({ limit: TO_CALL_ROWS })),
      unless(may('dashboard:read') && shown('visits'), () => adminApi.reports.funnel(lastDays(period))),
      unless(may('customers:read') && shown('myFollowUps'), () => adminApi.crm.followUps({ assignee: 'me', status: 'open', limit: MY_FOLLOW_UPS })),
    ]);

    const value = <T>(result: PromiseSettledResult<T | null>): T | null =>
      result.status === 'fulfilled' ? result.value : null;

    // Only a failed summary is worth an error banner — it is the headline.
    const failure = summary.status === 'rejected' ? summary.reason : null;

    set({
      summary: value(summary),
      trend: value(trend)?.items ?? [],
      topSellers: value(topSellers)?.items ?? [],
      queue: value(queue)?.items ?? [],
      lowStock: (value(lowStock)?.items ?? []).slice(0, LOW_STOCK_ROWS),
      recentOrders: value(orders)?.items ?? [],
      toCall: value(toCall)?.items ?? [],
      visits: value(visits),
      myFollowUps: value(myFollowUps)?.items ?? [],
      loading: false,
      refreshing: false,
      error: failure ? errorMessage(failure, 'Could not load the dashboard.') : null,
    });
  },
}));
